import { useQuery } from '@tanstack/react-query';
import { CourseSortColumn, Course, ListCoursesResponse } from '@ab/ams-school-api/types/private/v1';
import { config } from '~/config';
import { hasKey, IsNullOrUndefined } from '~/utils';
import { useRequest } from '~/lib/useRequest';

export type CoursesSearchQuery = {
  'filter[ids]'?: string[];
  'filter[isArchived]'?: boolean;
  'page[size]'?: number;
  'page[number]'?: number;
  sort?: CourseSortColumn;
};

export type SortableFields = CourseSortColumn;

const defaultValue: Course[] = [];

export function useCourses(
  query: CoursesSearchQuery = {},
  options?: { onSuccess?: (data: Course[], total: number) => void }
) {
  // default query parameters values
  if (!query['page[number]']) {
    query['page[number]'] = 1;
  }
  if (!query['page[size]']) {
    query['page[size]'] = 50;
  }
  if (!query.sort) {
    query.sort = 'name';
  }

  const request = useRequest();
  const { isLoading, isFetching, error, data, isPreviousData, isError } = useQuery(
    ['intakes', query],
    async () => {
      const response = await request<ListCoursesResponse>(
        `${config.apiHost}/private/v1/courses?${convertToUrlSearchParams(query)}`,
        {
          method: 'GET',
          isExpectedResponse,
        }
      );
      const data = response.data;
      const courses = data.map((coursesSearch) => {
        const attrs = coursesSearch.attributes;
        return {
          id: coursesSearch.id,
          code: attrs.code,
          name: attrs.name,
          isArchived: attrs.isArchived,
        };
      });

      // trigger on success
      options?.onSuccess?.(courses, response.meta.pagination.total);

      return {
        // as we are using keepPreviousData, we need to know the result is based on which page
        pagination: {
          pageNumber: response.meta.pagination.pageNumber,
          pageSize: response.meta.pagination.pageSize,
        },
        courses,
        totalCourses: response.meta.pagination.total,
      };
    },
    {
      keepPreviousData: true,
    }
  );

  return {
    isPreviousCoursesData: isPreviousData,
    coursesPagination: data?.pagination,
    courses: data?.courses ?? defaultValue,
    coursesError: error,
    isCoursesError: isError,
    isFetchingCourses: isFetching,
    isLoadingCourses: isLoading,
    totalCourses: data?.totalCourses ?? 0,
  };
}

function isExpectedResponse(res: unknown): res is ListCoursesResponse {
  return Boolean(res && typeof res === 'object' && hasKey('data', res) && Array.isArray(res.data));
}

const convertToUrlSearchParams = (params: CoursesSearchQuery, baseKey?: string) => {
  const queryParams = {} as Record<string, string>;
  for (const [k, v] of Object.entries(params)) {
    const key = (baseKey && `${baseKey}[${k}]`) || k;
    if (IsNullOrUndefined(v)) {
      continue;
    }
    if (typeof v === 'string' && v.length > 0) {
      queryParams[key] = v;
    } else if (typeof v === 'number') {
      queryParams[key] = `${v}`;
    } else if (typeof v === 'boolean' && v) {
      queryParams[key] = 't';
    } else if (Array.isArray(v) && v.length > 0) {
      queryParams[key] = v.join(',');
    }
  }
  return new URLSearchParams(queryParams);
};
